import {
  Box,
  Breadcrumbs,
  Theme,
  Typography,
  useTheme,
} from "@material-ui/core";
import { DatePicker } from "@material-ui/pickers";
import {
  Datagrid,
  Error,
  Filter,
  FunctionField,
  Link,
  Loading,
  NumberField,
  Record,
  ResourceContextProvider,
  TextField,
  useAuthenticated,
  useGetOne,
  useTranslate,
} from "react-admin";
import { RouteComponentProps } from "react-router-dom";
import resourceNames from "../../../../common/data/resourceNames";
import { CustomResourceRouteParams } from "../../../../common/types";
import { Void } from "../../../common/data-grid-fields/Void";
import { GenericDateOrTimePicker } from "../../../common/form/GenericDateOrTimePicker";
import { DvirHistoryListDto, VehicleDvirHistoryListDto } from "../types";
import ReportIcon from "@material-ui/icons/Report";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import { DefaultProLogsList } from "../../../common/list/DefaultProLogsList";
import { useActionPermissions } from "../../../../common/auth/useActionPermissions";
import permissionNames from "../../../../common/auth/access-control/permissionNames";
import ProLogsDateField from "../../../common/data-grid-fields/ProLogsDateField";

interface Props
  extends Partial<RouteComponentProps<CustomResourceRouteParams>> {}

interface BreadcrumbProps {
  vehicle?: DvirHistoryListDto;
}

const Breadcrumb = ({ vehicle }: BreadcrumbProps) => {
  const translate = useTranslate();
  return (
    <Breadcrumbs aria-label="breadcrumb">
      <Link to={`/${resourceNames.ASSETS_DVIR_HISTORY}`}>
        <Typography variant="h6">
          {translate("resources.assets-dvir-history.name")}
        </Typography>
      </Link>
      {vehicle && <Typography variant="h6">{vehicle?.vehicleName}</Typography>}
    </Breadcrumbs>
  );
};

const ListFilter = (props: any) => {
  return (
    <Filter {...props}>
      <GenericDateOrTimePicker
        PickerComponent={DatePicker}
        className="logs-list-view-filter"
        label="resources.assets-dvir-history.labels.dateFrom"
        source="dateFrom"
        alwaysOn
      />
      <GenericDateOrTimePicker
        PickerComponent={DatePicker}
        className="logs-list-view-filter"
        label="resources.assets-dvir-history.labels.dateTo"
        source="dateTo"
        alwaysOn
      />
    </Filter>
  );
};

export const VehicleDvirHistoryList = (props: Props) => {
  useAuthenticated();
  const translate = useTranslate();
  const theme: Theme = useTheme();
  const vehicleId = props.match?.params.id;
  const { loading, error, data } = useGetOne<DvirHistoryListDto & Record>(
    resourceNames.ASSETS_DVIR_HISTORY,
    vehicleId ?? 0
  );
  const permissions = useActionPermissions();

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Error error={error} />;
  }

  if (!data) {
    return null;
  }

  return (
    <div className="content-container">
      <ResourceContextProvider
        value={resourceNames.ASSETS_DVIR_VEHICLE_HISTORY}
      >
        <DefaultProLogsList
          title={<Breadcrumb vehicle={data} />}
          canExport={false}
          bulkActionButtons={false}
          actions={<Void />}
          filters={<ListFilter />}
          filter={{ vehicleId: props.match?.params.id }}
          sort={{ field: "date", order: "DESC" }}
          {...props}
        >
          <Datagrid
            rowClick={
              permissions[permissionNames.assets.dvirHistory.view]
                ? (id, _basePath, _record) => {
                    return data
                      ? `/${resourceNames.ASSETS_DVIR_HISTORY}/${data.id}/${id}`
                      : "";
                  }
                : undefined
            }
          >
            <ProLogsDateField
              source="date"
              label="resources.assets-dvir-vehicle-history.labels.date"
            />
            <TextField
              source="driverFullName"
              label="resources.assets-dvir-vehicle-history.labels.driverFullName"
            />
            <FunctionField<VehicleDvirHistoryListDto & Record>
              source="type"
              label="resources.assets-dvir-vehicle-history.labels.type"
              render={(record) =>
                record?.type && (
                  <Typography variant="body2">
                    {translate(
                      `resources.assets-dvir-vehicle-history.types.${record.type}`
                    )}
                  </Typography>
                )
              }
            />
            <TextField
              source="trailerName"
              label="resources.assets-dvir-vehicle-history.labels.trailerName"
            />
            <NumberField
              source="minorDefectsCount"
              label="resources.assets-dvir-vehicle-history.labels.minorDefectsCount"
            />
            <NumberField
              source="majorDefectsCount"
              label="resources.assets-dvir-vehicle-history.labels.majorDefectsCount"
            />
            <FunctionField<VehicleDvirHistoryListDto & Record>
              source="hasUnresolvedDefects"
              headerClassName="centered-content"
              label="resources.assets-dvir-history.labels.hasUnresolvedDefects"
              render={(record) => (
                <Box className="centered-content">
                  {record?.hasUnresolvedDefects ? (
                    <ReportIcon
                      style={{
                        color: theme.palette.error.main,
                      }}
                    />
                  ) : (
                    <CheckBoxIcon
                      style={{
                        color: theme.palette.success.main,
                      }}
                    />
                  )}
                </Box>
              )}
            />
          </Datagrid>
        </DefaultProLogsList>
      </ResourceContextProvider>
    </div>
  );
};
